import React, { useState, useEffect, useLayoutEffect } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import ReactEcharts from 'echarts-for-react';
import merge from 'lodash.merge';

import { Div } from '@pinpoint/design-kit';

import Wrapper from 'src/Wrapper';
import { traitsPropType, stylePropType } from 'src/PropTypes';
import { Style1 } from 'src/styles';
import { Palette } from 'src/components';

const FACTOR = 5;

function Bar({
  title,
  labels,
  series,
  style,
  options,
  formatter,
  showRange,
  onMouseover,
  theme: themeProp,
  oceanStyle,
  relative,
  onThemeChange,
  onOceanStyleChange,
  width,
  height,
  defaultTheme,
}) {
  const [theme, setTheme] = useState(themeProp);
  const [isOceanStyle, setOceanStyle] = useState(false);

  useEffect(() => {
    setTheme(themeProp);
  }, [themeProp]);

  useEffect(() => {
    setOceanStyle(oceanStyle);
  }, [oceanStyle]);

  const minTraitAxisValue = 0;
  const minRangeAxisValue = 50;
  let maxTraitAxisValue = 100;
  let maxRangeAxisValue = 100;
  const seriesList = [];
  if (relative) {
    const maxValue = series.reduce((currentMax, { values }) => {
      const localMax = Math.max(...values);
      return localMax > currentMax ? localMax : currentMax;
    }, 0);
    const rangeMaxValue = series.reduce((currentMax, { values }) => {
      const localMax = Math.max(...values.map((value) => 50 + Math.abs(50 - value)));
      return localMax > currentMax ? localMax : currentMax;
    }, 0);
    maxTraitAxisValue = maxValue % FACTOR === 0 ? maxValue : Math.floor(maxValue / FACTOR) * FACTOR + FACTOR;
    maxRangeAxisValue =
      rangeMaxValue % FACTOR === 0 ? rangeMaxValue : Math.floor(rangeMaxValue / FACTOR) * FACTOR + FACTOR;
  }
  for (let i = 0; i < series.length; i += 1) {
    seriesList.push({
      name: series[i].name,
      type: 'bar',
      itemStyle: {
        ...style.Bar.itemStyle,
      },
      tooltip: {
        ...style.global.tooltip,
        textStyle: {
          ...style.global.tooltip.textStyle,
        },
      },
      data: showRange
        ? series[i].values.map((x, index) => ({
            value: 50 + Math.abs(50 - x),
            itemStyle: {
              color: isOceanStyle ? labels[index].oceanColor || '#A0A0A0' : undefined,
            },
            tooltip: {
              formatter: isOceanStyle ? labels[index].factor.toUpperCase() : undefined,
            },
          }))
        : series[i].values.map((x, index) => ({
            value: x,
            itemStyle: {
              color: isOceanStyle ? labels[index].oceanColor || '#A0A0A0' : undefined,
            },
            tooltip: {
              formatter: isOceanStyle ? labels[index].factor.toUpperCase() : undefined,
            },
          })),
    });
  }
  const chartOption = {
    title: {
      text: title,
      ...style.global.title,
    },
    grid: {
      ...style.global.grid,
      ...style.Bar.grid,
      bottom: series.length > 1 ? 80 : 60,
    },
    textStyle: {
      ...style.global.textStyle,
    },
    label: {
      formatter,
      ...style.global.label,
      ...style.Bar.label,
    },
    backgroundColor: style.global.backgroundColor,
    toolbox: {
      ...style.global.toolbox,
    },
    legend: {
      data: series.map(({ name }) => name),
      show: series.length > 1,
      top: 'bottom',
    },
    xAxis: {
      ...style.Bar.xAxis,
      min: showRange ? minRangeAxisValue : minTraitAxisValue,
      max: showRange ? maxRangeAxisValue : maxTraitAxisValue,
    },
    yAxis: {
      ...style.Bar.yAxis,
      // NOTE: Typically if showRange is passed
      // the label should change
      data: labels.map((label, index) =>
        showRange ? (series[0].values[index] < 50 ? label.lowRangeName : label.highRangeName) : label.trait
      ),
    },
    tooltip: {
      trigger: isOceanStyle ? 'item' : 'none',
    },
    series: seriesList,
  };

  const mergedOptions = merge(chartOption, options);

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  });

  return (
    <Wrapper>
      <ReactEcharts
        data-test="test"
        ref={(element) => {
          const instance = element ? element.getEchartsInstance() : null;
          if (instance) {
            instance.off('mouseover');
            instance.on('mouseover', 'series', ({ dataIndex: index }) => {
              const label = labels[index];
              const values = series.map((_series) => _series.values[index]);
              onMouseover({ label, values });
            });
          }
        }}
        style={{
          width,
          height,
        }}
        option={mergedOptions}
        theme={theme}
        notMerge
      />
      <Div d="flex" h="2.5rem" justify="space-between">
        <Palette
          defaultTheme={defaultTheme}
          selected={theme}
          onSelect={(newTheme) => {
            setTheme(newTheme);
            setOceanStyle(false);
            onOceanStyleChange(false);
            onThemeChange(newTheme);
          }}
          style={{
            backgroundColor: mergedOptions.backgroundColor,
            width: '10rem',
            margin: '0 auto',
            paddingTop: '0.5rem',
          }}
        />
        <Div d="flex" justify="flex-end" m={{ l: '-2.5rem' }}>
          <Div
            tag="span"
            h="2.5rem"
            w="2.5rem"
            rounded="md"
            border="1px solid"
            borderColor="shadeE6"
            cursor="pointer"
            hoverBorderColor="shadeCC"
            data-tip="Add or remove OCEAN color coding"
            style={{
              ...style.COLORS.OCEAN_CSS,
            }}
            onClick={() => {
              setOceanStyle((currentStyle) => {
                onOceanStyleChange(!currentStyle);
                return !currentStyle;
              });
            }}
            m={{ l: '0.4rem' }}
          />
        </Div>
      </Div>
    </Wrapper>
  );
}

Bar.propTypes = {
  title: PropTypes.string,
  labels: traitsPropType.isRequired,
  series: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      values: PropTypes.arrayOf(PropTypes.number),
    })
  ).isRequired,
  style: stylePropType,
  options: PropTypes.shape({}),
  formatter: PropTypes.func,
  showRange: PropTypes.bool,
  onMouseover: PropTypes.func,
  theme: PropTypes.string,
  oceanStyle: PropTypes.bool,
  relative: PropTypes.bool,
  onThemeChange: PropTypes.func,
  onOceanStyleChange: PropTypes.func,
  width: PropTypes.string,
  height: PropTypes.string,
  defaultTheme: PropTypes.shape({}),
};

Bar.defaultProps = {
  title: '',
  style: Style1,
  options: {},
  formatter: (params) => params.value.toFixed(1),
  showRange: false,
  onMouseover: () => null,
  theme: 'pinpoint',
  oceanStyle: false,
  relative: false,
  onThemeChange: () => null,
  onOceanStyleChange: () => null,
  width: '100%',
  height: 'calc(100% - 2.5rem)',
};

export default Bar;
